// CANONICAL COPY (GH #7115). src/utils/frameworkEnabled.js re-exports this file so the browser and the
// Function App share one compliance engine — one definition of "compliant". Edit here, never there.
'use strict';

/**
 * src/utils/frameworkEnabled.js
 *
 * One answer to "does this tenant have this framework enabled?".
 *
 * The flag is OPT-OUT: a framework counts as enabled unless it is explicitly
 * disabled. That is the contract utils/frameworkCount.js states in prose and
 * that App.jsx's ControlFrameworkPreference provider, FrameworkMapView,
 * ControlIdBadge, FrameworkDetail and TreatmentControlPickerModal all read it
 * by, and tenant-signup seeds an explicit boolean per framework to match.
 *
 * It has to be opt-out because a framework can legitimately arrive without the
 * key at all: the super-admin push in TenantDetailPanel spreads a row straight
 * out of the global framework_catalogue table, which stores framework metadata
 * and no per-tenant flag, and FrameworkManager's "new framework" form creates
 * one without it too. On a tenant provisioned that way EVERY framework has
 * `enabled === undefined`.
 *
 * Four surfaces read the flag as truthy instead, so on exactly those tenants
 * they were silently, completely empty while every other surface rendered
 * normally: the Obligation Catalog showed "Catalog: 187, Enabled: 0" with no
 * obligation scopable at all (GH #5961), evidence-requirement and asset AI
 * classification lost their framework pickers, and the SKI 02.15 reference
 * field vanished from delivery agreements. Two readings of one fact is the
 * bug CLAUDE.md's "Enter Once, Know Everywhere" names; this module exists so
 * there is only one.
 *
 * The exception is a framework whose flag is a super-admin decision. CRA-B-001
 * makes fw-cra opt-in per tenant and never tenant self-service, so for it an
 * absent flag must NOT mean enabled — it means nobody has opted in yet. The
 * backend owns the same set in azure-functions/shared/frameworkGating.js for
 * the write paths; keep the two in sync.
 */

/** Framework ids whose `enabled` flag is a super-admin decision. */
const SUPER_ADMIN_ONLY_FRAMEWORK_IDS = new Set(['fw-cra']);

/**
 * True when the tenant has this framework enabled.
 *
 * @param {{ id?: string, enabled?: boolean } | null | undefined} fw
 */
function isFrameworkEnabled(fw) {
  if (!fw || !fw.id) return false;
  return SUPER_ADMIN_ONLY_FRAMEWORK_IDS.has(fw.id) ? fw.enabled === true : fw.enabled !== false;
}

/** The ids of every enabled framework in a tenant's `state.frameworks`. */
function enabledFrameworkIdSet(frameworks) {
  const ids = new Set();
  for (const fw of Array.isArray(frameworks) ? frameworks : []) {
    if (isFrameworkEnabled(fw)) ids.add(fw.id);
  }
  return ids;
}

module.exports = {
  SUPER_ADMIN_ONLY_FRAMEWORK_IDS,
  isFrameworkEnabled,
  enabledFrameworkIdSet,
};
